import type { Kana } from '@/data/gojuon'

/**
 * 視覺相似的假名映射（容易混淆的假名對）
 * 這些假名在視覺上非常相似，容易看錯
 */
export const visuallySimilarKana: Record<string, string[]> = {
  // 平假名視覺相似
  'あ': ['お', 'め'], // あ/お/め
  'お': ['あ', 'む'], // お/あ/む
  'め': ['ぬ', 'あ'], // め/ぬ/あ
  'ぬ': ['ね', 'め'], // ぬ/ね/め
  'ね': ['れ', 'ぬ'], // ね/れ/ぬ
  'れ': ['わ', 'ね'], // れ/わ/ね
  'わ': ['れ', 'ね'], // わ/れ/ね
  'る': ['ろ', 'う'], // る/ろ/う
  'ろ': ['る', 'の'], // ろ/る/の
  'の': ['め', 'ろ'], // の/め/ろ
  'は': ['ほ', 'け'], // は/ほ/け
  'ほ': ['は', 'ま'], // ほ/は/ま
  'け': ['は', 'げ'], // け/は/げ
  'き': ['さ', 'ち'], // き/さ/ち
  'さ': ['き', 'ち'], // さ/き/ち
  'ち': ['さ', 'き'], // ち/さ/き
  'し': ['じ', 'ひ'], // し/じ/ひ
  'ひ': ['し', 'ほ'], // ひ/し/ほ
  'み': ['に', 'め'], // み/に/め
  'に': ['み', 'ぬ'], // に/み/ぬ
  'て': ['と', 'そ'], // て/と/そ
  'と': ['て', 'ど'], // と/て/ど
  'そ': ['て', 'せ'], // そ/て/せ
  'せ': ['そ', 'ぜ'], // せ/そ/ぜ
  'す': ['ず', 'つ'], // す/ず/つ
  'つ': ['す', 'づ'], // つ/す/づ
  'く': ['け', 'ぐ'], // く/け/ぐ
  'か': ['が', 'け'], // か/が/け
  
  // 片假名視覺相似
  'ア': ['マ', 'オ'], // ア/マ/オ
  'オ': ['ア', 'マ'], // オ/ア/マ
  'マ': ['ア', 'ム'], // マ/ア/ム
  'ム': ['マ', 'メ'], // ム/マ/メ
  'メ': ['ナ', 'ム'], // メ/ナ/ム
  'ナ': ['メ', 'チ'], // ナ/メ/チ
  'チ': ['ナ', 'テ'], // チ/ナ/テ
  'テ': ['チ', 'ト'], // テ/チ/ト
  'ト': ['テ', 'ド'], // ト/テ/ド
  'ル': ['ロ', 'レ'], // ル/ロ/レ
  'ロ': ['ル', '口'], // ロ/ル
  'レ': ['ル', 'ワ'], // レ/ル/ワ
  'ワ': ['レ', 'ラ'], // ワ/レ/ラ
  'ラ': ['ワ', 'ル'], // ラ/ワ/ル
  'ハ': ['ホ', 'ヘ'], // ハ/ホ/ヘ
  'ホ': ['ハ', 'マ'], // ホ/ハ/マ
  'ヘ': ['ハ', 'ベ'], // ヘ/ハ/ベ
  'カ': ['ガ', 'ケ'], // カ/ガ/ケ
  'ケ': ['カ', 'ゲ'], // ケ/カ/ゲ
  'キ': ['ギ', 'サ'], // キ/ギ/サ
  'サ': ['ザ', 'キ'], // サ/ザ/キ
  'シ': ['ジ', 'ツ'], // シ/ジ/ツ
  'ツ': ['シ', 'ヅ'], // ツ/シ/ヅ
  'ソ': ['ゾ', 'ン'], // ソ/ゾ/ン
  'ン': ['ソ', 'ツ'], // ン/ソ/ツ
  'ス': ['ズ', 'ツ'], // ス/ズ/ツ
  'セ': ['ゼ', 'ソ'], // セ/ゼ/ソ
}

/**
 * 發音相似的假名映射（濁音/半濁音對應）
 * 這些假名發音相似，容易聽錯
 */
export const phoneticallySimilarKana: Record<string, string[]> = {
  // 清音/濁音對應（平假名）
  'か': ['が', 'き', 'く'], // か/が
  'が': ['か', 'ぎ', 'ぐ'],
  'き': ['ぎ', 'か', 'く'],
  'ぎ': ['き', 'が', 'ぐ'],
  'く': ['ぐ', 'か', 'け'],
  'ぐ': ['く', 'が', 'げ'],
  'け': ['げ', 'か', 'こ'],
  'げ': ['け', 'が', 'ご'],
  'こ': ['ご', 'か', 'け'],
  'ご': ['こ', 'が', 'げ'],
  
  'さ': ['ざ', 'し', 'す'],
  'ざ': ['さ', 'じ', 'ず'],
  'し': ['じ', 'さ', 'す'],
  'じ': ['し', 'ざ', 'ず'],
  'す': ['ず', 'さ', 'せ'],
  'ず': ['す', 'ざ', 'ぜ'],
  'せ': ['ぜ', 'さ', 'そ'],
  'ぜ': ['せ', 'ざ', 'ぞ'],
  'そ': ['ぞ', 'さ', 'せ'],
  'ぞ': ['そ', 'ざ', 'ぜ'],
  
  'た': ['だ', 'ち', 'つ'],
  'だ': ['た', 'ぢ', 'づ'],
  'ち': ['ぢ', 'た', 'つ'],
  'ぢ': ['ち', 'だ', 'づ'],
  'つ': ['づ', 'た', 'て'],
  'づ': ['つ', 'だ', 'で'],
  'て': ['で', 'た', 'と'],
  'で': ['て', 'だ', 'ど'],
  'と': ['ど', 'た', 'て'],
  'ど': ['と', 'だ', 'で'],
  
  'は': ['ば', 'ぱ', 'ひ'],
  'ば': ['は', 'ぱ', 'び'],
  'ぱ': ['は', 'ば', 'ぴ'],
  'ひ': ['び', 'ぴ', 'は'],
  'び': ['ひ', 'ば', 'ぴ'],
  'ぴ': ['ひ', 'ぱ', 'び'],
  'ふ': ['ぶ', 'ぷ', 'は'],
  'ぶ': ['ふ', 'ば', 'ぷ'],
  'ぷ': ['ふ', 'ぱ', 'ぶ'],
  'へ': ['べ', 'ぺ', 'は'],
  'べ': ['へ', 'ば', 'ぺ'],
  'ぺ': ['へ', 'ぱ', 'べ'],
  'ほ': ['ぼ', 'ぽ', 'は'],
  'ぼ': ['ほ', 'ば', 'ぽ'],
  'ぽ': ['ほ', 'ぱ', 'ぼ'],
  
  // 片假名對應
  'カ': ['ガ', 'キ', 'ク'],
  'ガ': ['カ', 'ギ', 'グ'],
  'キ': ['ギ', 'カ', 'ク'],
  'ギ': ['キ', 'ガ', 'グ'],
  'ク': ['グ', 'カ', 'ケ'],
  'グ': ['ク', 'ガ', 'ゲ'],
  'ケ': ['ゲ', 'カ', 'コ'],
  'ゲ': ['ケ', 'ガ', 'ゴ'],
  'コ': ['ゴ', 'カ', 'ケ'],
  'ゴ': ['コ', 'ガ', 'ゲ'],
  
  'サ': ['ザ', 'シ', 'ス'],
  'ザ': ['サ', 'ジ', 'ズ'],
  'シ': ['ジ', 'サ', 'ス'],
  'ジ': ['シ', 'ザ', 'ズ'],
  'ス': ['ズ', 'サ', 'セ'],
  'ズ': ['ス', 'ザ', 'ゼ'],
  'セ': ['ゼ', 'サ', 'ソ'],
  'ゼ': ['セ', 'ザ', 'ゾ'],
  'ソ': ['ゾ', 'サ', 'セ'],
  'ゾ': ['ソ', 'ザ', 'ゼ'],
  
  'タ': ['ダ', 'チ', 'ツ'],
  'ダ': ['タ', 'ヂ', 'ヅ'],
  'チ': ['ヂ', 'タ', 'ツ'],
  'ヂ': ['チ', 'ダ', 'ヅ'],
  'ツ': ['ヅ', 'タ', 'テ'],
  'ヅ': ['ツ', 'ダ', 'デ'],
  'テ': ['デ', 'タ', 'ト'],
  'デ': ['テ', 'ダ', 'ド'],
  'ト': ['ド', 'タ', 'テ'],
  'ド': ['ト', 'ダ', 'デ'],
  
  'ハ': ['バ', 'パ', 'ヒ'],
  'バ': ['ハ', 'パ', 'ビ'],
  'パ': ['ハ', 'バ', 'ピ'],
  'ヒ': ['ビ', 'ピ', 'ハ'],
  'ビ': ['ヒ', 'バ', 'ピ'],
  'ピ': ['ヒ', 'パ', 'ビ'],
  'フ': ['ブ', 'プ', 'ハ'],
  'ブ': ['フ', 'バ', 'プ'],
  'プ': ['フ', 'パ', 'ブ'],
  'ヘ': ['ベ', 'ペ', 'ハ'],
  'ベ': ['ヘ', 'バ', 'ペ'],
  'ペ': ['ヘ', 'パ', 'ベ'],
  'ホ': ['ボ', 'ポ', 'ハ'],
  'ボ': ['ホ', 'バ', 'ポ'],
  'ポ': ['ホ', 'パ', 'ボ'],
}

/**
 * 獲取與指定假名相似的假名列表（視覺相似 + 發音相似）
 * 返回相似假名的字符串數組
 */
export function getSimilarKanas(kana: Kana, mode: 'hiragana' | 'katakana'): string[] {
  const kanaText = mode === 'hiragana' ? kana.hiragana : kana.katakana
  const similar: string[] = []
  
  // 獲取視覺相似的假名
  const visualSimilar = visuallySimilarKana[kanaText] || []
  similar.push(...visualSimilar)
  
  // 獲取發音相似的假名
  const phoneticSimilar = phoneticallySimilarKana[kanaText] || []
  similar.push(...phoneticSimilar)
  
  // 去重
  return Array.from(new Set(similar))
}

/**
 * 從所有假名中找出與目標假名相似的假名
 */
export function findSimilarKanasFromPool(
  targetKana: Kana,
  allKanas: Kana[],
  mode: 'hiragana' | 'katakana'
): Kana[] {
  const similarTexts = getSimilarKanas(targetKana, mode)
  const targetText = mode === 'hiragana' ? targetKana.hiragana : targetKana.katakana
  
  return allKanas.filter(k => {
    const kText = mode === 'hiragana' ? k.hiragana : k.katakana
    return kText !== targetText && similarTexts.includes(kText)
  })
}

